import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownLeft, ArrowUpRight, Check, CheckCircle2, RefreshCw, Wallet } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { explain } from '../lib/api';
import { amount } from '../lib/format';
import { SNSC } from '../lib/native-token';
import { useLive } from '../lib/hooks';
import {
  readAccountWallet, readWalletTransfer, reviewWalletTransfer, sendWalletTransfer,
  type WalletAssetId, type WalletTransfer,
} from '../lib/account-wallet';
import { CopyButton, Empty, ErrorNotice, Loading } from '../components/common';

export function AccountPage() {
  const auth = useAuth();
  const [selected, setSelected] = useState<WalletAssetId>('SNSC');
  const [to, setTo] = useState(''), [quantity, setQuantity] = useState('');
  const [draft, setDraft] = useState<WalletTransfer | null>(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [success, setSuccess] = useState('');
  const currentOwner = useRef(auth.owner), mounted = useRef(true);
  currentOwner.current = auth.owner;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const state = useLive(async () => {
    if (!auth.owner) return null;
    const identity = await auth.getIdentity();
    if (identity.getPrincipal().toText() !== auth.owner) throw Error('Your signed-in account changed.');
    return readAccountWallet(identity);
  }, `account:${auth.owner}`, 15_000);
  useEffect(() => {
    setDraft(null); setError(''); setSuccess(''); setTo(''); setQuantity(''); setSelected('SNSC');
    if (!auth.owner) return;
    try {
      const pending = readWalletTransfer(auth.owner);
      if (pending) {
        setDraft(pending); setSelected(pending.asset); setTo(pending.to);
        setQuantity(amount(BigInt(pending.amount), 8, 8).replaceAll(',', ''));
      }
    } catch (reason) { setError(explain(reason)); }
  }, [auth.owner]);
  function choose(asset: WalletAssetId) {
    if (busy || draft?.attempted) return;
    setSelected(asset); setDraft(null); setTo(''); setQuantity(''); setError(''); setSuccess('');
  }
  function review(event: React.SubmitEvent) {
    event.preventDefault(); setError(''); setSuccess('');
    try {
      if (!state.data || state.data.owner !== auth.owner) throw Error('Wait for your wallet balance to load.');
      setDraft(reviewWalletTransfer(state.data, selected, to, quantity));
    } catch (reason) { setError(explain(reason)); }
  }
  async function send() {
    if (!draft || busy || !auth.owner) return;
    const pending = { ...draft, attempted: true }, owner = auth.owner;
    const isCurrent = () => mounted.current && currentOwner.current === owner;
    const assertCurrent = () => { if (!isCurrent()) throw Error('Your signed-in account changed. Open the original wallet to check this transfer.'); };
    setBusy(true); setError(''); setSuccess(''); setDraft(pending);
    try {
      const identity = await auth.getIdentity(); assertCurrent();
      const { block } = await sendWalletTransfer(identity, draft, assertCurrent);
      if (isCurrent()) {
        setDraft(null); setQuantity(''); setSuccess(`${pending.asset} sent · ledger block ${block}`); state.refresh();
      }
    } catch (reason) {
      if (isCurrent()) {
        try { setDraft(readWalletTransfer(owner)); setError(explain(reason)); }
        catch { setDraft(pending); setError(`${explain(reason)} Keep this original transfer open until its result is confirmed.`); }
      }
    } finally { if (isCurrent()) setBusy(false); }
  }
  if (!auth.owner) return <Empty title="Your wallet" icon={<Wallet size={30} />}>
    <p>Sign in to receive and send SNSC, Soonscreener’s native token, and ICP.</p>
    <button className="primary-button" disabled={auth.busy || !auth.ready} onClick={() => void auth.login()}>Sign in with Internet Identity</button>
  </Empty>;
  const asset = state.data?.assets[selected], locked = busy || Boolean(draft?.attempted);
  return <div className="account-page">
    <div className="page-heading">
      <div><div className="eyebrow"><Wallet size={14} />YOUR SOONSCREENER ACCOUNT</div><h1>Your wallet</h1><p>Your SNSC and ICP, together. Choose an asset to send or receive.</p></div>
      <button className="outline-button" disabled={state.loading || busy} onClick={state.refresh}><RefreshCw size={14} />Refresh</button>
    </div>
    {state.error && <ErrorNotice message={state.error} retry={state.refresh} />}
    {error && <ErrorNotice message={error} />}
    {success && <p className="success-notice" role="status"><Check size={15} />{success}</p>}
    {!state.data ? <Loading text="Reading your SNSC and ICP accounts…" /> : <>
      <div className="account-assets" role="group" aria-label="Wallet assets">
        {(['SNSC', 'ICP'] as const).map(id => {
          const token = state.data!.assets[id];
          return <button key={id} className="account-asset" aria-label={`Select ${id}`} aria-pressed={selected === id} disabled={locked} onClick={() => choose(id)} data-testid={`wallet-asset-${id}`}>
            <span className="account-asset-heading">
              <span className={`account-asset-mark ${id === 'ICP' ? 'icp' : ''}`} aria-hidden="true">{id === 'SNSC' ? <img src="/brand/owl.svg" alt="" /> : '∞'}</span>
              <span><strong>{id}</strong><small>{token.name}</small></span>
              {id === 'SNSC' && <span className="account-native-badge">NATIVE TOKEN</span>}
            </span>
            <strong className="account-asset-balance" data-testid={`wallet-balance-${id}`}>
              {token.balance === null ? '—' : amount(token.balance, token.decimals, token.decimals)}<small>{token.testMode ? `TEST ${id}` : id}</small>
            </strong>
            <span className="account-asset-footer">{id === 'SNSC' ? 'Reactions & holder tools' : 'Community profile updates'}{selected === id && <CheckCircle2 size={14} aria-label="Selected" />}</span>
            {token.error && <span className="account-asset-error" role="alert">{token.error}</span>}
          </button>;
        })}
      </div>
      <div className="account-native-note">
        <img src="/brand/owl.svg" alt="" />
        <div><strong>SNSC powers Soonscreener.</strong><p>Each token reaction costs 1 SNSC plus network fees. Hold 1,000 SNSC to unlock advanced alerts and saved screeners.</p></div>
        <Link to={SNSC.tokenPath}>Explore SNSC<ArrowUpRight size={13} /></Link>
      </div>
      {asset && <>
        <div className="account-selected-heading"><h2>{selected} account</h2><span data-testid="account-balance">Available: {asset.balance === null ? 'unavailable' : `${amount(asset.balance, 8, 8)} ${selected}`}</span></div>
        {asset.testMode && <p className="test-mode-banner">LOCAL TEST WALLET · This account uses test {selected}. No real {selected} transfers occur.</p>}
        <div className="account-wallet-grid">
          <section className="account-receive">
            <h2><ArrowDownLeft size={18} />Receive {selected}</h2>
            <p>{selected === 'SNSC' ? 'Send SNSC to your wallet principal using an ICRC-compatible wallet. Use the default account with no subaccount.' : 'Send ICP to your deposit account or to your wallet principal using an ICRC-compatible wallet.'} Your Internet Identity account is specific to Soonscreener.</p>
            {selected === 'ICP' && <>
              <label>ICP deposit account</label>
              {state.data.address ? <div className="account-address mono">{state.data.address}<CopyButton value={state.data.address} label="Copy ICP deposit account" /></div> : <ErrorNotice message={state.data.addressError || 'The ICP deposit address is unavailable. Your wallet principal below can receive ICRC transfers.'} />}
            </>}
            <label>{selected === 'SNSC' ? 'SNSC receiving principal' : 'Wallet principal'}{selected === 'SNSC' && <span className="account-icrc-tag">ICRC DEFAULT ACCOUNT</span>}</label>
            <div className="account-address mono"><span data-testid="account-owner">{auth.owner}</span><CopyButton value={auth.owner} label={`Copy ${selected} receiving principal`} /></div>
            <label>{selected} ledger canister</label>
            <div className="account-address mono"><span data-testid="wallet-ledger">{asset.ledger}</span><CopyButton value={asset.ledger} label={`Copy ${selected} ledger canister`} /></div>
            {selected === 'SNSC' && <p className="account-transfer-note">SNSC held in another wallet, such as Plug, stays in that wallet until you transfer it here.</p>}
          </section>
          <section className="account-send">
            <h2><ArrowUpRight size={18} />Send {selected}</h2>
            {!draft ? <form onSubmit={review}>
              <label>Recipient wallet principal<input required value={to} onChange={event => setTo(event.target.value)} placeholder="aaaaa-…-cai" autoComplete="off" spellCheck={false} /></label>
              <label>Amount {selected}<input required inputMode="decimal" value={quantity} onChange={event => setQuantity(event.target.value)} placeholder="0.00" autoComplete="off" /></label>
              <p>Ledger fee: {asset.fee === null ? 'unavailable' : `${amount(asset.fee, 8, 8)} ${selected}`}</p>
              <button className="primary-button" type="submit" disabled={asset.balance === null || asset.fee === null}>Review transfer</button>
            </form> : <>
              <dl className="payment-summary">
                <div><dt>Asset</dt><dd>{draft.asset}</dd></div>
                <div><dt>Recipient</dt><dd className="mono">{draft.to}</dd></div>
                <div><dt>Amount</dt><dd>{amount(BigInt(draft.amount), 8, 8)} {draft.asset}</dd></div>
                <div><dt>Ledger fee</dt><dd>{amount(BigInt(draft.fee), 8, 8)} {draft.asset}</dd></div>
                <div className="payment-total"><dt>Total debit</dt><dd>{amount(BigInt(draft.amount) + BigInt(draft.fee), 8, 8)} {draft.asset}</dd></div>
              </dl>
              <div className="editor-actions">
                <button className="primary-button" disabled={busy} onClick={() => void send()}>{busy ? 'Confirming transfer…' : draft.attempted ? 'Retry original transfer' : `Confirm send ${draft.asset}`}</button>
                {!draft.attempted && <button className="outline-button" disabled={busy} onClick={() => setDraft(null)}>Back</button>}
              </div>
              {draft.attempted && <p className="account-transfer-note account-pending-note">This transfer is awaiting confirmation. Its asset, recipient, amount and fee stay fixed. Retrying checks the original transfer; do not send it again from another wallet.</p>}
            </>}
          </section>
        </div>
      </>}
    </>}
  </div>;
}
